import React, { useState } from 'react'
import { apiClient } from '../api/client'
import { Badge } from './ui/Badge'
import type { TranslationMeta } from '../types'

const SUPPORTED_LANGUAGES: Record<string, string> = {
  es: 'Spanish', fr: 'French', pt: 'Portuguese', ar: 'Arabic',
  sw: 'Swahili', hi: 'Hindi', ht: 'Haitian Creole', am: 'Amharic',
  so: 'Somali', zh: 'Chinese (Simplified)', bn: 'Bengali', de: 'German',
  ja: 'Japanese', ko: 'Korean', ru: 'Russian', it: 'Italian',
  nl: 'Dutch', tr: 'Turkish', vi: 'Vietnamese', th: 'Thai',
  lg: 'Luganda', hu: 'Hungarian',
}

interface TranslationManagerProps {
  surveyId: string
  availableLanguages: string[]
  translationMetadata: Record<string, TranslationMeta>
  onLanguagesChanged: () => void
  onReview: (lang: string) => void
}

const TranslationManager: React.FC<TranslationManagerProps> = ({
  surveyId,
  availableLanguages,
  translationMetadata,
  onLanguagesChanged,
  onReview,
}) => {
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
  const [context, setContext] = useState('')
  const [translating, setTranslating] = useState(false)
  const [error, setError] = useState('')
  const [removing, setRemoving] = useState<string | null>(null)

  const translatedLanguages = availableLanguages.filter(l => l !== 'en')
  const untranslatedLanguages = Object.keys(SUPPORTED_LANGUAGES).filter(
    l => !availableLanguages.includes(l)
  )

  const handleTranslate = async () => {
    if (selectedLanguages.length === 0) return
    setTranslating(true)
    setError('')
    try {
      await apiClient.post(`/surveys/${surveyId}/translate`, {
        target_languages: selectedLanguages,
        context: context || undefined,
      })
      setSelectedLanguages([])
      setContext('')
      onLanguagesChanged()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Translation failed')
    } finally {
      setTranslating(false)
    }
  }

  const handleRemove = async (lang: string) => {
    if (!confirm(`Remove ${SUPPORTED_LANGUAGES[lang] || lang} translation?`)) return
    setRemoving(lang)
    try {
      await apiClient.delete(`/surveys/${surveyId}/translations/${lang}`)
      onLanguagesChanged()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove translation')
    } finally {
      setRemoving(null)
    }
  }

  const toggleLanguage = (lang: string) => {
    setSelectedLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    )
  }

  const statusBadge = (meta: TranslationMeta | undefined) => {
    if (!meta) return <Badge variant="neutral">Unknown</Badge>
    switch (meta.status) {
      case 'approved': return <Badge variant="success">Approved</Badge>
      case 'draft': return <Badge variant="warning">Draft</Badge>
      case 'pending': return <Badge variant="info">Translating...</Badge>
      default: return <Badge variant="neutral">{meta.status}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      {/* Existing translations */}
      <div className="bg-surface rounded-xl border border-border-light p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Translations</h3>

        {translatedLanguages.length === 0 ? (
          <p className="text-foreground-muted text-sm">
            No translations yet. Add a language below to get started.
          </p>
        ) : (
          <div className="space-y-3">
            {translatedLanguages.map(lang => {
              const meta = translationMetadata[lang]
              return (
                <div
                  key={lang}
                  className="flex items-center justify-between py-3 px-4 bg-background rounded-lg border border-border-light"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-foreground">
                      {SUPPORTED_LANGUAGES[lang] || lang.toUpperCase()}
                    </span>
                    {statusBadge(meta)}
                    {meta?.translated_at && (
                      <span className="text-xs text-foreground-muted">
                        {new Date(meta.translated_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onReview(lang)}
                      className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      Review
                    </button>
                    <button
                      onClick={() => handleRemove(lang)}
                      disabled={removing === lang}
                      className="px-3 py-1.5 text-sm font-medium text-error hover:bg-error-light rounded-lg transition-colors disabled:opacity-50"
                    >
                      {removing === lang ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add language */}
      <div className="bg-surface rounded-xl border border-border-light p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Add Language</h3>

        {untranslatedLanguages.length === 0 ? (
          <p className="text-foreground-muted text-sm">All supported languages have been added.</p>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Select languages to translate
              </label>
              <div className="flex flex-wrap gap-2">
                {untranslatedLanguages.map(lang => (
                  <button
                    key={lang}
                    onClick={() => toggleLanguage(lang)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      selectedLanguages.includes(lang)
                        ? 'bg-primary text-white border-primary'
                        : 'bg-surface text-foreground border-border hover:border-primary'
                    }`}
                  >
                    {SUPPORTED_LANGUAGES[lang]}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-1">
                Context <span className="text-foreground-muted font-normal">(optional)</span>
              </label>
              <textarea
                value={context}
                onChange={e => setContext(e.target.value)}
                placeholder="e.g., Maslow hierarchy of needs assessment for global well-being measurement"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                rows={2}
              />
              <p className="text-xs text-foreground-muted mt-1">
                Helps the AI produce more accurate translations for domain-specific terms.
              </p>
            </div>

            {error && (
              <div className="mb-4 bg-error-light border border-error text-error px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleTranslate}
              disabled={selectedLanguages.length === 0 || translating}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {translating ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Translating {selectedLanguages.length} language{selectedLanguages.length > 1 ? 's' : ''}...
                </span>
              ) : (
                `Translate to ${selectedLanguages.length} language${selectedLanguages.length !== 1 ? 's' : ''}`
              )}
            </button>
            {translating && (
              <p className="text-xs text-foreground-muted mt-2">
                AI translation typically takes 15-45 seconds. Please wait...
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default TranslationManager
